import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link2, Copy, RotateCw, Trash2, ExternalLink, Loader2 } from "lucide-react";

/**
 * Share-link manager for a single quote.
 *
 * The link is `{FRONTEND_URL}/proposal/{token}` — anyone with the URL
 * can view the proposal but can't touch anything else on the site.
 *
 * Actions:
 *   - Generate on first open (auto-created if none exists)
 *   - Copy to clipboard
 *   - Rotate (issues a new token, previous URLs 404)
 *   - Revoke (removes the token — link stops working)
 */
export default function ShareLinkDialog({ open, onOpenChange, quoteId, canEdit }) {
  const [state, setState] = useState({ loading: false, token: null, url: null });

  const fetchToken = async (rotate = false) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.post(
        `/quotes/${quoteId}/share-token${rotate ? "?rotate=true" : ""}`,
      );
      setState({ loading: false, token: data.token, url: data.url });
      if (rotate) toast.success("New share link generated — old link no longer works");
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create link");
    }
  };

  useEffect(() => {
    if (open && quoteId) fetchToken(false);
    if (!open) setState({ loading: false, token: null, url: null });
  }, [open, quoteId]);

  const copy = async () => {
    if (!state.url) return;
    try {
      await navigator.clipboard.writeText(state.url);
      toast.success("Link copied to clipboard");
    } catch {
      // Fallback for older browsers / insecure contexts.
      const inp = document.createElement("input");
      inp.value = state.url;
      document.body.appendChild(inp);
      inp.select();
      document.execCommand("copy");
      document.body.removeChild(inp);
      toast.success("Link copied to clipboard");
    }
  };

  const revoke = async () => {
    try {
      await api.delete(`/quotes/${quoteId}/share-token`);
      toast.success("Share link revoked");
      setState({ loading: false, token: null, url: null });
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not revoke");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#C4D0DB] text-[#011836] max-w-xl"
        data-testid="share-link-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#011836] flex items-center gap-2">
            <Link2 className="h-4 w-4 text-[#0088CC]" />
            Share this proposal
          </DialogTitle>
          <DialogDescription className="text-[#5B6B7C]">
            Copy the link below to share this proposal with anyone. They will
            only be able to view this one proposal &mdash; nothing else on the
            site.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {state.loading && !state.url ? (
            <div className="flex items-center gap-2 text-sm text-[#5B6B7C] py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating link…
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <Input
                  value={state.url || ""}
                  readOnly
                  onFocus={(e) => e.currentTarget.select()}
                  className="bg-white border-[#C4D0DB] text-[#011836] font-mono text-xs"
                  data-testid="share-link-input"
                />
                <Button
                  onClick={copy}
                  className="bg-[#0088CC] hover:bg-[#006DA6] text-white shrink-0"
                  data-testid="share-link-copy-btn"
                >
                  <Copy className="h-4 w-4 mr-1.5" />
                  Copy
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => window.open(state.url, "_blank", "noopener")}
                  className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
                  data-testid="share-link-open-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-1.5" />
                  Preview
                </Button>
                {canEdit && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => fetchToken(true)}
                      disabled={state.loading}
                      className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
                      data-testid="share-link-rotate-btn"
                    >
                      <RotateCw className="h-4 w-4 mr-1.5" />
                      Rotate
                    </Button>
                    <Button
                      variant="outline"
                      onClick={revoke}
                      className="border-[#EF4444]/40 text-[#EF4444] hover:bg-[#FDECEC]"
                      data-testid="share-link-revoke-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-1.5" />
                      Revoke
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
